if (!customElements.get('bundle-product')) {
  class BundleProduct extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      // Never leave the price skeleton up if the widgets fail to load
      setTimeout(() => this.setAttribute('data-price-ready', ''), 5000);

      if (window['bundle:upsellsReady']) {
        this.init();
        return;
      }
      document.addEventListener('bundle:upsellsReady', () => this.init(), { once: true });

      const isMixed =
        this.dataset.bundleType === 'multipack' &&
        (this.dataset.selectionMode || 'uniform') === 'mixed';
      if (isMixed) return;

      const trySelfInit = () => {
        if (this._inited) return true;
        if (this.querySelector('intersection-loader')) return false; // slots still loading
        if (!this.querySelector('product-upsells[data-color-collection]')) return false;
        this.init();
        return true;
      };
      if (trySelfInit()) return;
      const readyObserver = new MutationObserver(() => {
        if (trySelfInit()) readyObserver.disconnect();
      });
      readyObserver.observe(this, { childList: true, subtree: true });
    }

    init() {
      if (this._inited) return;
      this._inited = true;
      this._isMultipack = this.dataset.bundleType === 'multipack';
      this._selectionMode = this.dataset.selectionMode || 'uniform';
      const rawPackSizes = JSON.parse(this.dataset.packSizes || 'null');
      this._packSizes = (Array.isArray(rawPackSizes) ? rawPackSizes.filter(n => n > 0) : [1])
        .sort((a, b) => a - b);
      if (!this._packSizes.length) this._packSizes = [1];
      this._packSize = this._packSizes[0];

      const packSizeSelector = this.querySelector('[data-pack-size-selector]');
      if (packSizeSelector) {
        packSizeSelector.addEventListener('change', e => {
          if (e.target.name === '_pack_size') {
            this._packSize = Number(e.target.value);
            if (this._isMultipack && this._selectionMode === 'mixed') {
              this._rebuildMixedSlots(this._packSize);
            } else {
              this.update();
            }
          }
        });
      }

      // No pre-selection — user must choose color and size explicitly.
      // Mixed mode clears selectedProduct separately in _initMixedSharedSize.
      this.querySelectorAll('product-upsells[data-color-collection]').forEach(upsell => {
        upsell.querySelectorAll('input[data-action="product-select"]').forEach(r => r.checked = false);
        upsell.querySelectorAll('[data-display-color]').forEach(el => el.textContent = '');
        upsell.currentVariant = null;
      });

      this.update();
      this._pruneToBundleColors();

      if (this._isMultipack && this._selectionMode === 'mixed') {
        this.querySelectorAll('product-upsells').forEach((upsell, slotIdx) => {
          upsell.querySelectorAll('input[type="radio"]').forEach(input => {
            if (input.name) input.name = `${input.name}--s${slotIdx}`;
          });
        });
        this._initMixedSharedSize();
      }

      if (this._isMultipack && this._selectionMode !== 'mixed') {
        this._hideMultipackExtras();
      }

      this._slotImages = new Map();
      this._originalSlides = null;

      this.addEventListener('change', e => {
        this._clearBundleErrors();
        // Pack size changes are handled by the packSizeSelector listener; calling
        // update() here too would race against _rebuildMixedSlots (which clears
        // all upsells) and momentarily flash $0 on the price display.
        if (!e.target.closest('[data-pack-size-selector]')) {
          this.update();
        }
        if (e.target.matches('input[data-action="product-select"]')) {
          this._onColorPicked(e.target);
        }
      });

      this.addEventListener('click', async e => {
        const atcButton = e.target.closest('[data-action="add-to-cart"]');
        if (!atcButton) return;

        if (this._showBundleErrors()) return;
        if (this._hasOosSelection()) return;

        atcButton.classList.add('loading');
        // Unique per-click ID so multiple bundles of the same type can coexist in the cart
        const bundleId = `${this.dataset.bundleHandle}_${Date.now().toString(36)}`;
        let items;

        const packSizeDiscountsRaw = this.dataset.packSizeDiscounts;
        const discountPct = this._getDiscountPercent();
        const bundleProps = {
          ...(discountPct ? { _discount: String(discountPct) } : {}),
          ...(packSizeDiscountsRaw ? { _pack_size_discounts: packSizeDiscountsRaw } : {}),
        };

        if (this._isMultipack && this._selectionMode === 'mixed') {
          const allUpsells = [...this.querySelectorAll('product-upsells[data-color-collection]')];
          const packSize = allUpsells.length;
          items = allUpsells.map(u => ({
            id: u.currentVariant.id,
            qty: 1,
            properties: { _bundle_id: bundleId, _bundle_size: packSize, ...bundleProps },
          }));
        } else if (this._isMultipack) {
          const upsell = this.querySelector('product-upsells[data-color-collection]');
          const packSize = this._packSize;
          items = [{
            id: upsell.currentVariant.id,
            qty: packSize,
            properties: { _bundle_id: bundleId, _bundle_size: packSize, ...bundleProps },
          }];
        } else {
          const allUpsells = [...this.querySelectorAll('product-upsells[data-color-collection]')];
          const bundleSize = allUpsells.length;
          items = allUpsells.map(({ currentVariant }) => ({
            id: currentVariant.id,
            qty: 1,
            properties: { _bundle_id: bundleId, _bundle_size: bundleSize, ...bundleProps },
          }));
        }

        window.dispatchEvent(new CustomEvent('minicart:bulkadd', { detail: { items } }));

        setTimeout(() => {
          atcButton.classList.remove('loading');
        }, 1000);
      });

      this._applyUrlPreselect();
    }

    // Optional one-time preselect via ?swatch=<colorName>, driven by product-tile links.
    // Matches by color name (the same value each product-select radio already carries)
    // rather than product id, so it applies to every slot that offers this color as an
    // option regardless of which specific product represents it there — sidesteps any
    // need to know which product ids exist in which slot. Runs last so it isn't wiped by
    // the "no pre-selection" reset earlier in init() or by _initMixedSharedSize()'s own
    // reset. Best-effort: slots that don't offer this color are left untouched.
    _applyUrlPreselect() {
      const swatchColor = new URLSearchParams(window.location.search).get('swatch');
      if (!swatchColor) return;

      const target = swatchColor.trim().toLowerCase();
      this.querySelectorAll('input[data-action="product-select"]').forEach(input => {
        if ((input.value || '').trim().toLowerCase() === target) input.click();
      });
    }

    _initMixedSharedSize() {
      // Idempotent: remove any existing picker/label from a previous call
      this.querySelector('[data-shared-size-picker]')?.remove();
      this.querySelector('[data-step2-label]')?.remove();

      const upsells = [...this.querySelectorAll('product-upsells[data-color-collection]')];
      if (!upsells.length) return;

      // Find the size option name from the first slot's option-choice inputs
      const firstSizeInput = upsells[0].querySelector('input[data-action="option-choice"][data-option-type]');
      if (!firstSizeInput) return;
      this._sizeOptionName = firstSizeInput.dataset.optionType;

      // Collect unique size values in DOM order from the first slot
      const sizeValues = [...new Set(
        [...upsells[0].querySelectorAll(`input[data-action="option-choice"][data-option-type="${this._sizeOptionName}"]`)]
          .map(i => i.dataset.option)
          .filter(Boolean)
      )];
      if (!sizeValues.length) return;

      const upsellWrap = this.querySelector('[data-upsell-widget-wrap]');
      if (!upsellWrap) return;

      const picker = document.createElement('div');
      picker.setAttribute('data-shared-size-picker', '');
      picker.className = 'py-8 md:border-b-2 max-md:border-b-2 max-md:mb-5 border-tertiaryLight';
      picker.innerHTML = `
        <strong class="text-lg uppercase block pb-3">Step 1: Pick your size</strong>
        <div class="flex flex-wrap gap-2">
          ${sizeValues.map(s => `
            <div class="relative flex-shrink-0">
              <input type="radio" name="_shared_bundle_size" value="${s}" class="absolute top-0 left-0 w-full h-full z-10 cursor-pointer opacity-0 peer">
              <span class="border border-secondaryDark rounded-full px-4 py-2 text-sm font-semibold peer-checked:border-primaryDark peer-checked:bg-primaryLight transition-all duration-200 block">${s}</span>
            </div>
          `).join('')}
        </div>
      `;

      const step2Label = document.createElement('strong');
      step2Label.setAttribute('data-step2-label', '');
      step2Label.className = 'text-lg uppercase block pt-8 pb-3';
      step2Label.textContent = 'Step 2: Pick your colors';

      upsellWrap.before(picker);
      upsellWrap.before(step2Label);

      upsells.forEach(upsell => {
        // Hide image, title, reviews, size options — only color swatches remain
        upsell.classList.add(
          '!block',
        );

        // No auto-color: clear visual selection and internal state
        upsell.querySelectorAll('input[data-action="product-select"]').forEach(r => r.checked = false);
        upsell.selectedProduct = null;
        upsell.currentVariant = null;
      });

      picker.addEventListener('change', e => {
        if (e.target.name === '_shared_bundle_size') {
          this._sharedSize = e.target.value;
          this._applySharedSize();
          this._syncMixedColorAvailability();
          this.update();
        }
      });
    }

    _applySharedSize() {
      if (!this._sharedSize || !this._sizeOptionName) return;
      this.querySelectorAll('product-upsells[data-color-collection]').forEach(upsell => {
        // Check the hidden size radio so product-upsells picks it up on color selection
        const radio = upsell.querySelector(
          `input[data-action="option-choice"][data-option-type="${this._sizeOptionName}"][data-option="${this._sharedSize}"]`
        );
        if (radio && !radio.checked) radio.checked = true;

        // If a color is already selected, update currentVariant immediately
        const product = upsell.selectedProduct;
        const variant = this._getVariantForSharedSize(product);
        if (variant) upsell.currentVariant = variant;

        if (typeof upsell.enableOrDisableOptionSelects === 'function') {
          upsell.enableOrDisableOptionSelects();
        }
      });
    }

    _normalizeOptionValue(value) {
      return (value ?? '').toString().trim().toLowerCase();
    }

    _isVariantAvailableForQty(variant, qty = 1) {
      if (!variant) return false;
      if (variant.available === false) return false;
      if (
        variant.inventory_management === 'shopify' &&
        variant.inventory_policy !== 'continue' &&
        typeof variant.inventory_quantity === 'number' &&
        variant.inventory_quantity < qty
      ) {
        return false;
      }
      return true;
    }

    _getVariantForSharedSize(product) {
      if (!product?.variants || !product?.options_with_values || !this._sharedSize || !this._sizeOptionName) {
        return null;
      }

      const sizeIdx = product.options_with_values.findIndex(
        ({ name }) => this._normalizeOptionValue(name) === this._normalizeOptionValue(this._sizeOptionName)
      );
      if (sizeIdx < 0) return null;

      return product.variants.find((variant) =>
        this._normalizeOptionValue(variant?.options?.[sizeIdx]) === this._normalizeOptionValue(this._sharedSize)
      ) || null;
    }

    _syncMixedColorAvailability() {
      if (!this._isMultipack || this._selectionMode !== 'mixed') return;

      this.querySelectorAll('product-upsells[data-color-collection]').forEach(upsell => {
        const selectionInputs = upsell.querySelectorAll('input[data-action="product-select"][data-product-id]');

        selectionInputs.forEach(input => {
          const productId = input.dataset.productId;
          const product = upsell.products?.find(({ id }) => String(id) === String(productId));
          const variant = this._sharedSize ? this._getVariantForSharedSize(product) : null;
          const isEnabled = !this._sharedSize || this._isVariantAvailableForQty(variant, 1);
          const listItem = input.closest('li');
          const container = listItem || input.closest('label, .swatch, [data-product-select-item]');

          input.setAttribute('aria-disabled', String(!isEnabled));

          if (listItem) listItem.classList.toggle('oos', !isEnabled);
          if (container && container !== listItem) container.classList.toggle('unavailable', !isEnabled);

          if (!isEnabled && input.checked) input.checked = false;

          if (!isEnabled && String(upsell.selectedProduct?.id) === String(productId)) {
            upsell.selectedProduct = null;
            upsell.currentVariant = null;
          }
        });

        if (upsell.selectedProduct) {
          const selectedVariant = this._sharedSize
            ? this._getVariantForSharedSize(upsell.selectedProduct)
            : null;

          if (selectedVariant && this._isVariantAvailableForQty(selectedVariant, 1)) {
            upsell.currentVariant = selectedVariant;
          } else if (this._sharedSize) {
            upsell.currentVariant = null;
          }
        }
      });
    }

    // The upsell widget renders every product in the color collection; keep only
    // the colors explicitly listed in the bundle metafield.
    _pruneToBundleColors() {
      const bundleProducts = window.bundleProducts || [];
      this.querySelectorAll('[data-action="product-select"][data-product-id]').forEach(el => {
        const productId = el.dataset.productId;
        if (!productId || !bundleProducts.find(({ id }) => Number(id) === Number(productId))) el.closest('li')?.remove();
      });
      this.querySelectorAll('[data-color-group-section]').forEach(section => {
        if (!section.querySelector('li')) section.classList.add('hidden');
      });
    }

    _rebuildMixedSlots(packSize) {
      const upsellWrap = this.querySelector('[data-upsell-widget-wrap]');
      if (!upsellWrap) return;

      const products = window.bundleProducts || [];
      const colorGroupHandles = typeof getColorGroupHandles === 'function' ? getColorGroupHandles(products) : [];
      const productsByColorGroup = typeof groupProductsByColorGroup === 'function' ? groupProductsByColorGroup(products) : {};
      if (!colorGroupHandles.length) return;

      // Reset shared size and carousel when slot count changes
      this._sharedSize = null;
      this._slotImages = new Map();
      this._originalSlides = null;
      this._updateCarousel();

      const slotClass = '[&_::after]:hidden [&_.oos]:after:block [&_product-upsells]:!block [&_product-upsells]:!py-0 [&_[data-image-wrapper]]:hidden [&_[data-display-title]]:hidden [&_[data-oke-star-rating]]:hidden [&_[data-display-options]]:hidden';

      // Load one slot per color group — the rest are cloned after the fetch resolves
      upsellWrap.innerHTML = colorGroupHandles.flatMap(colorGroupHandle => {
        const group = productsByColorGroup[colorGroupHandle];
        if (!group || !group[0]) return [];
        const [primaryProduct] = group;
        return [`
          <div data-bundle-slot="0" data-color-group="${colorGroupHandle}" class="${slotClass}">
            <intersection-loader
              data-url="/collections/${colorGroupHandle}/products/${primaryProduct.handle}"
              data-section-id="bundle-product-upsell-widget"
              data-load-immediately="true"
            ></intersection-loader>
          </div>
        `];
      }).join('');

      const observer = new MutationObserver(() => {
        const loaders = upsellWrap.querySelectorAll('intersection-loader').length;
        if (loaders > 0) return;

        const firstSlots = [...upsellWrap.querySelectorAll('[data-bundle-slot="0"]')];
        if (firstSlots.length < colorGroupHandles.length) return;
        if (firstSlots.some(s => !s.querySelector('product-upsells'))) return;

        firstSlots.forEach(slot => {
          for (let i = packSize - 1; i >= 1; i--) {
            const clone = slot.cloneNode(true);
            clone.dataset.bundleSlot = i;
            slot.after(clone);
          }
        });

        observer.disconnect();

        this._pruneToBundleColors();
        this.querySelectorAll('product-upsells').forEach((upsell, slotIdx) => {
          upsell.querySelectorAll('input[type="radio"]').forEach(input => {
            if (input.name) input.name = `${input.name}--s${slotIdx}`;
          });
          upsell.querySelectorAll('[data-display-color]').forEach(el => el.textContent = '');
        });

        this._initMixedSharedSize();
        this._syncMixedColorAvailability();
        this.update();
      });

      observer.observe(upsellWrap, { childList: true, subtree: true });
    }

    _hideMultipackExtras() {
      this.querySelectorAll('product-upsells[data-color-collection]').forEach(upsell => {
        upsell.classList.add(
          '!block',
          '[&_[data-image-wrapper]]:hidden',
          '[&_[data-display-title]]:hidden',
          '[&_[data-oke-star-rating]]:hidden',
        );
      });
    }

    // Mirrors the bundler-discount function: once a bundle has any pack size discounts,
    // only the selected size's entry applies (missing = no discount) and the base
    // discount is ignored. The base discount is used only when there are none.
    _getDiscountPercent() {
      const packSizeDiscountsRaw = this.dataset.packSizeDiscounts;
      if (packSizeDiscountsRaw) {
        try {
          const psd = JSON.parse(packSizeDiscountsRaw);
          if (psd && typeof psd === 'object' && Object.keys(psd).length) {
            const currentPackSize = this._packSize ?? Number(this.dataset.packSize);
            return Number(psd[String(currentPackSize)] || 0);
          }
        } catch {}
      }
      return Number(this.dataset.discount || 0);
    }

    _getDiscountMultiplier() {
      return (100 - this._getDiscountPercent()) / 100;
    }

    update() {
      const productSelectors = [...this.querySelectorAll('product-upsells[data-color-collection]')];
      let accumulatedPrice = 0;
      let accumulatedCompareAtPrice = 0;

      productSelectors.forEach(x => {
        const currentVariant = x.currentVariant;
        const selectedProduct = x.selectedProduct;
        if (!currentVariant) {
          // For mixed multipack: use the representative product price so the
          // discounted total is visible as soon as pack size is chosen
          if (this._isMultipack && this._selectionMode === 'mixed') {
            // The slot's products come from the whole color collection, which can include
            // products that aren't in the bundle (e.g. a pre-packed 3-pack), so take the
            // representative price from a bundle member.
            const bundleProducts = window.bundleProducts || [];
            const repProduct = selectedProduct
              ?? x.products?.find(p => bundleProducts.some(({ id }) => Number(id) === Number(p.id)))
              ?? x.products?.[0];
            const basePrice = repProduct?.price ?? 0;
            accumulatedPrice += basePrice;
            accumulatedCompareAtPrice += repProduct?.compare_at_price || basePrice;
            return;
          }

          // Non-mixed / bundle types: if a product is known (but variant
          // not selected yet), use the product's base price as a best-effort
          // representative so price display is available.
          if (selectedProduct) {
            const basePrice = selectedProduct.price ?? 0;
            accumulatedPrice += basePrice;
            accumulatedCompareAtPrice += selectedProduct.compare_at_price || basePrice;
          }
          return;
        }
        const fullPrice = (currentVariant.price ?? selectedProduct?.price ?? 0);
        accumulatedPrice += fullPrice;
        accumulatedCompareAtPrice += currentVariant.compare_at_price || fullPrice;
      });

      // Scale price by pack size for uniform multipack bundles
      if (this._isMultipack && this._selectionMode !== 'mixed' && this._packSize > 1) {
        accumulatedPrice *= this._packSize;
        accumulatedCompareAtPrice *= this._packSize;
      }

      const discountPercentage = this._getDiscountMultiplier();

      // The bundle's own compare-at (set in the bundler app) is the original
      // full price, prior to product-level markdowns. Prefer it for the strikethrough.
      let bundleCompareAt = Number(this.dataset.compareAtPrice) || 0;
      if (bundleCompareAt && this._isMultipack && this._selectionMode !== 'mixed' && this._packSize > 1) {
        bundleCompareAt *= this._packSize;
      }
      if (bundleCompareAt <= accumulatedPrice * discountPercentage) bundleCompareAt = 0;
      const strikethroughPrice = bundleCompareAt || (discountPercentage < 1 ? accumulatedPrice : accumulatedCompareAtPrice);

      this.querySelectorAll('[data-display-price] [data-full-price]').forEach(el => {
        if (bundleCompareAt || discountPercentage < 1) {
          const value = (strikethroughPrice / 100).toFixed(2).replace(/\.00$/, '');
          el.innerText = `$${value}`;
        } else if (accumulatedPrice < accumulatedCompareAtPrice) {
          const value = (accumulatedCompareAtPrice / 100).toFixed(2).replace(/\.00$/, '');
          el.innerText = `$${value}`;
        } else {
          el.innerText = ``;
        }
      });

      const discountedResult = Math.ceil((accumulatedPrice * discountPercentage / 100) * 100) / 100;
      const discountedValue = `$${discountedResult.toFixed(2).replace(/\.00$/, '')}`;

      const showingStrikethrough = discountedResult * 100 < strikethroughPrice;
      this.querySelectorAll('[data-display-price] [data-discounted-price]').forEach(el => {
        el.innerText = discountedValue;
        // Only the top-of-page sale price is red, not the price inside the add-to-cart button
        el.classList.toggle('text-crimsonRed', showingStrikethrough && !el.closest('[data-action="add-to-cart"]'));
      });
      // First computed price replaces the server-rendered placeholder skeleton
      this.setAttribute('data-price-ready', '');

      // Update desktop sticky ATC bar in header (outside this element)
      const stickyDiscounted = document.querySelector('[data-sticky-atc-discounted-price]');
      const stickyFull = document.querySelector('[data-sticky-atc-full-price]');
      if (stickyDiscounted) stickyDiscounted.innerText = discountedValue;
      if (stickyFull) {
        const showFull = bundleCompareAt
          ? true
          : discountPercentage < 1
            ? accumulatedPrice > 0
            : accumulatedPrice < accumulatedCompareAtPrice;
        if (showFull) {
          const fullVal = bundleCompareAt || discountPercentage < 1
            ? `$${(strikethroughPrice / 100).toFixed(2).replace(/\.00$/, '')}`
            : `$${(accumulatedCompareAtPrice / 100).toFixed(2).replace(/\.00$/, '')}`;
          stickyFull.innerText = fullVal;
          stickyFull.classList.remove('hidden');
        } else {
          stickyFull.innerText = '';
          stickyFull.classList.add('hidden');
        }
      }

      const atcButton = this.querySelector('[data-action="add-to-cart"]');
      if (atcButton) atcButton.toggleAttribute('disabled', this._hasOosSelection());
    }

    _hasOosSelection() {
      return [...this.querySelectorAll('product-upsells[data-color-collection]')]
        .some(upsell => upsell.currentVariant && !upsell.currentVariant.available);
    }

    _onColorPicked(input) {
      const upsell = input.closest('product-upsells');
      if (!upsell) return;

      const allUpsells = [...this.querySelectorAll('product-upsells[data-color-collection]')];
      const slotIndex = allUpsells.indexOf(upsell);
      if (slotIndex < 0) return;

      const productId = input.dataset.productId;
      const product = upsell.products?.find(p => String(p.id) === String(productId));
      if (!product) return;

      this._slotImages.set(slotIndex, {
        src: product.featured_image,
        alt: product.featured_image_alt || product.title || '',
        media: product.media || [],
      });

      this._updateCarousel();
    }

    _updateCarousel() {
      const allUpsells = [...this.querySelectorAll('product-upsells[data-color-collection]')];
      const totalSlots = allUpsells.length;

      // Collect all swipers to update
      const desktopMain = this.querySelector('[data-swiper-images-alt][data-main-image-container]');
      const desktopThumbs = this.querySelector('[data-swiper-images-alt][data-thumbnail-container]');
      const mobileMain = this.querySelector('[data-mobile-image-swipers] swiper-container[data-product-images]');
      const mobileThumbs = this.querySelector('[data-mobile-image-swipers] swiper-container.thumbs');

      const swiperPairs = [
        [desktopMain, desktopThumbs],
        [mobileMain, mobileThumbs],
      ].filter(([main]) => main);

      // Save original slides once
      if (!this._originalSlides && swiperPairs[0]?.[0]) {
        this._originalSlides = swiperPairs.map(([main, thumbs]) => ({
          main: main?.innerHTML,
          thumbs: thumbs?.innerHTML,
        }));
      }

      const anyPicked = this._slotImages.size > 0;

      if (!anyPicked) {
        // Restore originals
        swiperPairs.forEach(([main, thumbs], i) => {
          if (main && this._originalSlides?.[i]?.main != null) main.innerHTML = this._originalSlides[i].main;
          if (thumbs && this._originalSlides?.[i]?.thumbs != null) thumbs.innerHTML = this._originalSlides[i].thumbs;
          main?.swiper?.update();
          thumbs?.swiper?.update();
        });
        return;
      }

      // Build one slide per picked slot only
      const mainSlides = Array.from({ length: totalSlots }, (_, i) => {
        const img = this._slotImages.get(i);
        if (!img?.src) return '';
        return `<swiper-slide class="swiper-slide aspect-[9/10] rounded-lg w-full overflow-hidden bg-secondaryLight">
          <img src="${img.src}" alt="${img.alt}" loading="lazy" class="mix-blend-multiply object-cover object-top w-full h-full">
        </swiper-slide>`;
      }).join('');

      const thumbSlides = Array.from({ length: totalSlots }, (_, i) => {
        const img = this._slotImages.get(i);
        if (!img?.src) return '';
        return `<swiper-slide class="swiper-slide aspect-[9/10] rounded overflow-hidden w-1/5">
          <img src="${img.src}" alt="${img.alt}" loading="lazy" class="object-cover object-top w-full h-full">
        </swiper-slide>`;
      }).join('');

      swiperPairs.forEach(([main, thumbs]) => {
        if (thumbs) { thumbs.innerHTML = thumbSlides; thumbs.swiper?.update(); }
        if (main) {
          main.innerHTML = mainSlides;
          main.swiper?.update();
          main.swiper?.thumbs?.swiper?.update();
          main.swiper?.slideTo(0, 0);
        }
      });
    }

    _clearBundleErrors() {
      this.querySelectorAll('product-upsells.selection-needed').forEach(upsell => {
        if (upsell.currentVariant) upsell.classList.remove('selection-needed');
      });
      if (this._sharedSize) {
        const sharedSizePicker = this.querySelector('[data-shared-size-picker]');
        if (sharedSizePicker) {
          sharedSizePicker.classList.remove('border', '!border-red-500', 'rounded-lg');
          sharedSizePicker.querySelector('[data-shared-size-error]')?.classList.add('hidden');
        }
      }
    }

    _showBundleErrors() {
      let hasErrors = false;

      if (this._isMultipack && this._selectionMode === 'mixed' && !this._sharedSize) {
        const picker = this.querySelector('[data-shared-size-picker]');
        if (picker) {
          picker.classList.add('border', '!border-red-500', 'rounded-lg');
          let errEl = picker.querySelector('[data-shared-size-error]');
          if (!errEl) {
            errEl = document.createElement('div');
            errEl.setAttribute('data-shared-size-error', '');
            errEl.className = 'text-red-500 text-sm mt-2';
            errEl.textContent = 'Please select a size.';
            picker.appendChild(errEl);
          } else {
            errEl.classList.remove('hidden');
          }
        }
        hasErrors = true;
      }

      this.querySelectorAll('product-upsells[data-color-collection]').forEach(upsell => {
        if (upsell.currentVariant) return;
        upsell.classList.add('selection-needed');
        hasErrors = true;
      });

      return hasErrors;
    }
  }

  customElements.define('bundle-product', BundleProduct);
}
